import { useState, useCallback, useEffect } from "react";
import { apiFetch } from "../../lib/api";

interface WallpaperMeta {
  id: string;
  name: string;
  thumbnail: string; // inline SVG preview or URL
  url: string;       // path to full wallpaper SVG
  style: string;     // e.g. "circuit-board", "nebula", "minimal"
}

const BUILT_IN: WallpaperMeta[] = [
  {
    id: "circuit-board",
    name: "Circuit Board",
    thumbnail: "",
    url: "/wallpapers/circuit-board.svg",
    style: "circuit-board",
  },
  {
    id: "nebula",
    name: "Deep Space Nebula",
    thumbnail: "",
    url: "/wallpapers/nebula.svg",
    style: "nebula",
  },
  {
    id: "grid-city",
    name: "Grid City",
    thumbnail: "",
    url: "/wallpapers/grid-city.svg",
    style: "cyberpunk",
  },
  {
    id: "topology",
    name: "Topology",
    thumbnail: "",
    url: "/wallpapers/topology.svg",
    style: "network",
  },
  {
    id: "minimal",
    name: "Minimal",
    thumbnail: "",
    url: "/wallpapers/minimal.svg",
    style: "minimal",
  },
];

function WallpaperCard({
  wp,
  isActive,
  onApply,
}: {
  wp: WallpaperMeta;
  isActive: boolean;
  onApply: () => void;
}) {
  return (
    <div
      className="wp-card"
      style={{
        border: isActive ? "2px solid var(--brand-blue, #4f8fff)" : "1px solid rgba(79, 143, 255, 0.15)",
        borderRadius: 12,
        overflow: "hidden",
        background: "rgba(10, 10, 20, 0.8)",
        cursor: "pointer",
        transition: "all 0.2s ease",
      }}
      onClick={onApply}
    >
      <div
        style={{
          height: 120,
          background: `url(${wp.url}) center/cover`,
          borderBottom: "1px solid rgba(79, 143, 255, 0.1)",
        }}
      />
      <div style={{ padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: "var(--brand-white, #e6edf3)" }}>{wp.name}</div>
          <div style={{ fontSize: 11, color: "var(--brand-muted, #484f58)", marginTop: 2 }}>{wp.style}</div>
        </div>
        {isActive && (
          <span style={{
            fontSize: 10,
            fontWeight: 600,
            padding: "2px 8px",
            borderRadius: 4,
            background: "rgba(79, 143, 255, 0.15)",
            color: "var(--brand-blue, #4f8fff)",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}>
            Active
          </span>
        )}
      </div>
    </div>
  );
}

export function WallpaperWorkbench() {
  const [wallpapers, setWallpapers] = useState<WallpaperMeta[]>(BUILT_IN);
  const [activeId, setActiveId] = useState<string>("circuit-board");
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState("");

  // Load saved wallpapers from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem("opentool-wallpapers");
      if (saved) {
        const parsed = JSON.parse(saved) as WallpaperMeta[];
        setWallpapers([...BUILT_IN, ...parsed]);
      }
    } catch { /* ignore */ }

    const active = localStorage.getItem("opentool-active-wallpaper");
    if (active) setActiveId(active);
  }, []);

  const handleApply = useCallback((wp: WallpaperMeta) => {
    setActiveId(wp.id);
    localStorage.setItem("opentool-active-wallpaper", wp.id);

    // Dispatch event so DesktopBackground can pick it up
    window.dispatchEvent(new CustomEvent("opentool-wallpaper-change", { detail: { url: wp.url, id: wp.id } }));
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setGenStatus("Generating wallpaper...");

    try {
      const res = await apiFetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [
            {
              role: "system",
              content: `You are a wallpaper generator. Generate a pure SVG wallpaper (viewBox="0 0 1920 1080") based on the user's prompt. Output ONLY the SVG markup, no explanation. Use these brand colors: blue #4f8fff, teal #7dcfff, green #34d399, purple #a78bfa. Background should be dark (#000 to #0a0a14). Make it dense, detailed, and visually striking.`,
            },
            { role: "user", content: prompt },
          ],
        }),
      });

      const data = await res.json();
      const svgMatch = data?.choices?.[0]?.message?.content?.match(/<svg[\s\S]*<\/svg>/);

      if (svgMatch) {
        const svgBlob = new Blob([svgMatch[0]], { type: "image/svg+xml" });
        const url = URL.createObjectURL(svgBlob);
        const id = `gen-${Date.now()}`;
        const name = prompt.slice(0, 30) + (prompt.length > 30 ? "..." : "");

        const newWp: WallpaperMeta = { id, name, thumbnail: "", url, style: "generated" };
        setWallpapers((prev) => [...prev, newWp]);

        // Save to localStorage (sans blob URL — store SVG content)
        try {
          const saved = JSON.parse(localStorage.getItem("opentool-wallpapers") || "[]");
          saved.push({ ...newWp, url: `data:image/svg+xml;base64,${btoa(svgMatch[0])}` });
          localStorage.setItem("opentool-wallpapers", JSON.stringify(saved));
        } catch { /* ignore */ }

        setGenStatus("Wallpaper generated!");
        handleApply(newWp);
      } else {
        setGenStatus("Failed — model didn't return valid SVG");
      }
    } catch (e) {
      setGenStatus(`Error: ${e}`);
    } finally {
      setGenerating(false);
    }
  }, [prompt, handleApply]);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: "1rem", padding: "1rem" }}>
      {/* Header */}
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: "var(--brand-white, #e6edf3)" }}>
          Wallpaper Studio
        </h2>
        <p style={{ fontSize: 12, color: "var(--brand-muted, #484f58)", margin: "4px 0 0" }}>
          Generate and apply desktop wallpapers. Describe what you want and the AI will create it.
        </p>
      </div>

      {/* Prompt bar */}
      <div style={{ display: "flex", gap: 8 }}>
        <input
          type="text"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
          placeholder="Describe a wallpaper... e.g. 'cyberpunk city skyline with neon grid'"
          style={{
            flex: 1,
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid rgba(79, 143, 255, 0.2)",
            background: "rgba(10, 10, 20, 0.6)",
            color: "var(--brand-white, #e6edf3)",
            fontFamily: "var(--ot-font-mono, monospace)",
            fontSize: 12,
            outline: "none",
          }}
        />
        <button
          onClick={handleGenerate}
          disabled={generating || !prompt.trim()}
          style={{
            padding: "8px 20px",
            borderRadius: 8,
            border: "none",
            background: generating ? "rgba(79, 143, 255, 0.2)" : "var(--brand-blue, #4f8fff)",
            color: "#fff",
            fontFamily: "var(--ot-font-mono, monospace)",
            fontSize: 12,
            fontWeight: 600,
            cursor: generating ? "wait" : "pointer",
          }}
        >
          {generating ? "Generating..." : "Generate"}
        </button>
      </div>

      {genStatus && (
        <div style={{ fontSize: 11, color: "var(--brand-teal, #7dcfff)" }}>{genStatus}</div>
      )}

      {/* Wallpaper grid */}
      <div
        style={{
          flex: 1,
          overflow: "auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: 12,
          alignContent: "start",
        }}
      >
        {wallpapers.map((wp) => (
          <WallpaperCard
            key={wp.id}
            wp={wp}
            isActive={activeId === wp.id}
            onApply={() => handleApply(wp)}
          />
        ))}
      </div>
    </div>
  );
}
